import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { 
  FaStar, FaEdit, FaTrash, FaPlus, FaTimes, FaUser,
  FaRegStar
} from "react-icons/fa";
import { 
  fetchRating, 
  submitRating, 
  deleteRating 
} from "../../Redux/ratingSlice";

const CATEGORY_LABELS = {
  1: 'Medicine',
  2: 'Test',
  3: 'Package'
};

const CATEGORY_ICONS = {
  1: '💊',
  2: '🔬',
  3: '📦'
};

export default function UniversalRating({ 
  productId,
  categoryId,
  showLabel = true,
  onClose,
  size = 'md'     // sm, md, lg
}) {
  const dispatch = useDispatch();
  
  const ratings = useSelector((state) => state.rating.ratings || {});
  const ratingLoading = useSelector((state) => state.rating.loading);
  const submitLoading = useSelector((state) => state.rating.submitLoading);
  const deleteLoading = useSelector((state) => state.rating.deleteLoading);
  
  const [selectedRating, setSelectedRating] = useState(0);
  const [averageRating, setAverageRating] = useState(0);
  const [hover, setHover] = useState(null);
  const [review, setReview] = useState("");
  const [ratingList, setRatingList] = useState([]);
  const [showRatingSection, setShowRatingSection] = useState(false);
  const [myRatingId, setMyRatingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);

  const user = JSON.parse(localStorage.getItem("medarphaUser") || "{}");
  const userId = Number(user?.userId);

  const categoryLabel = CATEGORY_LABELS[categoryId] || 'Product';
  const categoryIcon = CATEGORY_ICONS[categoryId] || '📦';

  // ✅ Size Classes – MORE COMPACT
  const sizeClasses = {
    sm: {
      container: 'p-2 space-y-1.5',
      header: 'text-[10px]',
      stars: 12,
      button: 'text-[9px] py-0.5 px-2',
      review: 'text-[9px]',
      summary: 'text-[9px]',
      textarea: 'text-[10px] p-1.5',
      spacing: 'gap-1',
      badge: 'text-[8px]',
    },
    md: {
      container: 'p-2.5 space-y-2',
      header: 'text-[11px]',
      stars: 14,
      button: 'text-[10px] py-1 px-2.5',
      review: 'text-[10px]',
      summary: 'text-[10px]',
      textarea: 'text-[11px] p-2',
      spacing: 'gap-1.5',
      badge: 'text-[9px]',
    },
    lg: {
      container: 'p-3 space-y-2.5',
      header: 'text-xs',
      stars: 16,
      button: 'text-xs py-1.5 px-3',
      review: 'text-[11px]',
      summary: 'text-[11px]',
      textarea: 'text-xs p-2.5',
      spacing: 'gap-2',
      badge: 'text-[10px]',
    }
  };

  const sizes = sizeClasses[size] || sizeClasses.md;

  const getRatingFromStore = () => {
    const key = `${productId}_${categoryId}`;
    return ratings[key] || { average: 0, total: 0, ratings: [] };
  };

  const fetchRatings = async () => {
    if (!productId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setApiError(null);
      
      await dispatch(fetchRating({ 
        productId: Number(productId), 
        categoryId: categoryId 
      }));
      
      const ratingData = getRatingFromStore();
      setRatingList(ratingData.ratings || []);
      setAverageRating(ratingData.average || 0);
      
      const myRatings = (ratingData.ratings || []).filter((r) => {
        const createdBy = r.created_by || r.CreatedBy || r.userId || r.UserId;
        return Number(createdBy) === userId;
      });
      
      if (myRatings.length) {
        const latest = myRatings.sort((a, b) => {
          const dateA = new Date(a.created_date || a.CreatedDate || a.date || 0);
          const dateB = new Date(b.created_date || b.CreatedDate || b.date || 0);
          return dateB - dateA;
        })[0];
        setMyRatingId(latest.id || latest.Id);
        setSelectedRating(latest.ratingpoint || latest.RatingPoint || 0);
        setReview(latest.remark || latest.Remark || "");
      } else {
        setMyRatingId(null);
        setSelectedRating(0);
        setReview("");
      }
      
    } catch (err) {
      console.error(`❌ Fetch ${categoryLabel} error:`, err);
      setApiError(`Failed to load ${categoryLabel} ratings`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!productId) {
      setLoading(false);
      return;
    }
    fetchRatings();
  }, [productId, categoryId]);

  const handleSubmitRating = async () => {
    if (!selectedRating) {
      alert(`Please select rating for ${categoryLabel}`);
      return;
    }
    if (!userId) {
      alert("User not logged in");
      return;
    }

    try {
      await dispatch(submitRating({
        ratingId: myRatingId ? Number(myRatingId) : 0,
        remark: review || "",
        ratingpoint: Number(selectedRating),
        userId: Number(userId),
        categoryId: categoryId,
        productId: Number(productId),
      })).unwrap();
      
      alert(`✅ ${categoryLabel} rating ${myRatingId ? 'updated' : 'submitted'} successfully!`);
      
      await fetchRatings();
      setShowRatingSection(false);
      
    } catch (err) {
      console.error(`❌ Submit ${categoryLabel} error:`, err);
      alert(`Failed to submit ${categoryLabel} rating`);
    }
  };

  const handleDeleteRating = async () => {
    if (!myRatingId) {
      alert("No rating to delete");
      return;
    }
    if (!window.confirm(`Delete your ${categoryLabel} rating?`)) {
      return;
    }

    try {
      await dispatch(deleteRating({
        ratingId: myRatingId,
        productId: Number(productId),
        categoryId: categoryId
      })).unwrap();
      
      setSelectedRating(0);
      setReview("");
      setMyRatingId(null);
      setShowRatingSection(false);
      alert(`✅ ${categoryLabel} rating deleted successfully!`);
      
      await fetchRatings();
      
    } catch (err) {
      console.error(`❌ Delete ${categoryLabel} error:`, err);
      alert(`Failed to delete ${categoryLabel} rating`);
    }
  };

  const handleCancel = () => {
    setShowRatingSection(false);
    if (myRatingId) {
      const ratingData = getRatingFromStore();
      const myRating = (ratingData.ratings || []).find(r => {
        const createdBy = r.created_by || r.CreatedBy || r.userId || r.UserId;
        return Number(createdBy) === userId;
      });
      if (myRating) {
        setSelectedRating(myRating.ratingpoint || myRating.RatingPoint || 0);
        setReview(myRating.remark || myRating.Remark || "");
      }
    } else {
      setSelectedRating(0);
      setReview("");
    }
  };

  const renderStars = (rating, starSize = 14, interactive = false) => {
    const stars = [];
    const roundedRating = Math.round(rating || 0);
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <FaStar
          key={i}
          size={starSize}
          className={`${interactive ? 'cursor-pointer transition-all hover:scale-110' : ''}`}
          color={i <= (interactive ? (hover || selectedRating) : roundedRating) ? "#facc15" : "#d1d5db"}
          onMouseEnter={interactive ? () => setHover(i) : undefined}
          onMouseLeave={interactive ? () => setHover(null) : undefined}
          onClick={interactive ? () => setSelectedRating(i) : undefined}
        />
      );
    }
    return stars;
  };

  if (loading || ratingLoading) {
    return (
      <div className={`bg-white rounded-xl border shadow-sm ${sizes.container}`}>
        {showLabel && (
          <div className="flex items-center justify-between">
            <h5 className={`font-semibold text-gray-800 ${sizes.header}`}>
              {categoryIcon} {categoryLabel} Ratings
            </h5>
            <div className="w-3 h-3 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}
        <div className="h-12 bg-gray-50 rounded-lg animate-pulse"></div>
      </div>
    );
  }

  if (apiError) {
    return (
      <div className={`bg-white rounded-xl border shadow-sm ${sizes.container}`}>
        {showLabel && (
          <h5 className={`font-semibold text-gray-800 ${sizes.header}`}>
            {categoryIcon} {categoryLabel} Ratings
          </h5>
        )}
        <div className="text-center py-2">
          <p className={`text-red-500 ${sizes.review}`}>⚠️ {apiError}</p>
          <button 
            onClick={fetchRatings}
            className={`mt-1 bg-blue-500 text-white px-2 py-0.5 rounded hover:bg-blue-600 ${sizes.button}`}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl border shadow-sm ${sizes.container}`}>

      {/* Header */}
      <div className="flex items-center justify-between">
        {showLabel && (
          <h5 className={`font-semibold text-gray-800 ${sizes.header} flex items-center gap-1.5`}>
            <span>{categoryIcon}</span>
            {categoryLabel} Ratings
            {ratingList.length > 0 && (
              <span className={`text-gray-400 font-normal ${sizes.header}`}>
                ({ratingList.length})
              </span>
            )}
          </h5>
        )}

        {ratingList.length > 0 && (
          <div className="flex items-center gap-1">
            <span className={`font-bold text-gray-800 ${sizes.header}`}>
              {averageRating.toFixed(1)}
            </span>
            <FaStar className="text-yellow-500" size={sizes.stars - 2} />
          </div>
        )}
      </div>

      {/* Rating Summary Bar - compact */}
      {ratingList.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-1.5">
          <div className="space-y-0.5">
            {[5, 4, 3, 2, 1].map((star) => {
              const count = ratingList.filter(r => 
                Math.round(r.ratingpoint || r.RatingPoint || 0) === star
              ).length;
              const percentage = ratingList.length > 0 ? (count / ratingList.length) * 100 : 0;
              return (
                <div key={star} className="flex items-center gap-1.5">
                  <span className={`text-gray-500 w-2.5 ${sizes.summary}`}>{star}</span>
                  <div className="flex-1 h-1 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-yellow-500 rounded-full transition-all duration-300"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className={`text-gray-400 w-4 text-right ${sizes.summary}`}>{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Add/Edit Button */}
      {!showRatingSection && (
        <button
          onClick={() => setShowRatingSection(true)}
          className={`w-full rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-1.5 ${sizes.button} ${
            myRatingId 
              ? 'border border-blue-500 text-blue-500 hover:bg-blue-50' 
              : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 shadow-sm hover:shadow'
          }`}
        >
          {myRatingId ? (
            <>
              <FaEdit size={sizes.stars - 4} />
              Edit Rating
            </>
          ) : (
            <>
              <FaPlus size={sizes.stars - 4} />
              Rate
            </>
          )}
        </button>
      )}

      {/* Rating Input */}
      {showRatingSection && (
        <div className="bg-gray-50 rounded-lg p-2 border border-gray-200 space-y-1.5">
          <div className="flex items-center justify-between">
            <h6 className={`font-semibold text-gray-700 ${sizes.header}`}>
              {myRatingId ? '✏️ Edit' : '⭐ Add Rating'}
            </h6>
            <button onClick={handleCancel} className="text-gray-400 hover:text-gray-600">
              <FaTimes size={sizes.stars - 4} />
            </button>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex gap-0.5">
              {renderStars(selectedRating, sizes.stars + 4, true)}
            </div>
            <span className={`text-gray-500 ${sizes.review}`}>
              {selectedRating > 0 ? `${selectedRating}/5` : 'Select'}
            </span>
          </div>

          <textarea
            className={`w-full border rounded-lg ${sizes.textarea} focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none transition-all`}
            placeholder={`Share your experience...`}
            value={review}
            onChange={(e) => setReview(e.target.value)}
            rows={2}
          />

          <div className="flex gap-1.5">
            <button
              onClick={handleSubmitRating}
              disabled={!selectedRating || submitLoading}
              className={`flex-1 rounded-lg font-medium transition-all duration-200 ${sizes.button} ${
                selectedRating && !submitLoading
                  ? 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700 shadow-sm hover:shadow' 
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {submitLoading ? '...' : (myRatingId ? 'Update' : 'Submit')}
            </button>

            {myRatingId && (
              <button
                onClick={handleDeleteRating}
                disabled={deleteLoading}
                className={`px-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-all duration-200 flex items-center gap-0.5 ${sizes.button}`}
              >
                <FaTrash size={sizes.stars - 8} />
                {deleteLoading ? '...' : ''}
              </button>
            )}
          </div>
        </div>
      )}

      {/* All Reviews - compact */}
      {ratingList.length > 0 && !showRatingSection && (
        <div className="space-y-1.5">
          <h6 className={`font-semibold text-gray-700 flex items-center gap-1.5 ${sizes.review}`}>
            <span>📝 Reviews</span>
            <span className="text-gray-400 font-normal">({ratingList.length})</span>
          </h6>

          <div className="space-y-1.5 max-h-48 overflow-y-auto pr-0.5">
            {ratingList.slice(0, 5).map((r, index) => {
              const isMyRating = Number(r.created_by || r.CreatedBy || r.userId || r.UserId) === userId;
              const ratingPoint = r.ratingpoint || r.RatingPoint || 0;
              const remark = r.remark || r.Remark || "";
              const userName = r.userName || r.UserName || r.created_by || r.CreatedBy || "User";
              const createdDate = r.created_date || r.CreatedDate || r.date;

              return (
                <div
                  key={r.id || r.Id || index}
                  className={`border rounded-lg p-1.5 transition-all duration-200 ${
                    isMyRating ? 'bg-blue-50 border-blue-200' : 'bg-white hover:bg-gray-50'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <div className={`rounded-full bg-gradient-to-r from-blue-500 to-blue-600 text-white flex items-center justify-center font-bold ${sizes.review}`} style={{ width: sizes.stars - 2, height: sizes.stars - 2 }}>
                          {userName.charAt(0).toUpperCase()}
                        </div>
                        <span className={`font-medium text-gray-700 ${sizes.review}`}>
                          {userName}
                        </span>
                        {isMyRating && (
                          <span className={`bg-blue-100 text-blue-700 px-1 py-0.5 rounded-full font-medium ${sizes.badge}`}>
                            You
                          </span>
                        )}
                      </div>
                      
                      <div className="flex gap-0.5 mt-0.5">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <FaStar
                            key={s}
                            size={sizes.stars - 6}
                            className={s <= ratingPoint ? "text-yellow-500" : "text-gray-300"}
                          />
                        ))}
                      </div>

                      {remark && (
                        <p className={`text-gray-600 mt-0.5 leading-relaxed ${sizes.review}`}>
                          {remark}
                        </p>
                      )}
                    </div>

                    {createdDate && (
                      <span className={`text-gray-400 whitespace-nowrap ml-1.5 ${sizes.review}`}>
                        {new Date(createdDate).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
            {ratingList.length > 5 && (
              <div className={`text-center text-gray-400 ${sizes.review}`}>
                +{ratingList.length - 5} more reviews
              </div>
            )}
          </div>
        </div>
      )}

      {/* No Reviews - compact */}
      {ratingList.length === 0 && !showRatingSection && (
        <div className="text-center py-2">
          <FaRegStar className="text-gray-300 mx-auto" size={sizes.stars + 2} />
          <p className={`text-gray-400 mt-0.5 ${sizes.review}`}>No reviews yet</p>
          <p className={`text-gray-300 ${sizes.review}`}>Be the first to rate</p>
        </div>
      )}
    </div>
  );
}